import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash-es'
import { api } from 'components/api'
import { surveySearchPath } from 'components/routes/api'

export interface SurveyResult {
  id: number
  slug: string
  name: string
}

interface SurveySearchProps {
  onSelect: (survey: SurveyResult) => void
}

type SearchState =
  | { status: 'idle', results: SurveyResult[] }
  | { status: 'searching', results: SurveyResult[] }
  | { status: 'success', results: SurveyResult[] }
  | { status: 'error', results: SurveyResult[] }

const IDLE: SearchState = { status: 'idle', results: [] }

const SurveySearch: React.FC<SurveySearchProps> = ({ onSelect }) => {
  const [searchState, setSearchState] = useState<SearchState>(IDLE)
  const latestSearchId = useRef(0)

  const performSearch = useCallback((search: string) => {
    const searchId = ++latestSearchId.current

    if (search.trim() === '') {
      setSearchState(IDLE)
      return
    }

    setSearchState((prev) => ({ status: 'searching', results: prev.results }))

    api.get<unknown, SurveyResult[]>({ url: surveySearchPath(), params: { search } })
      .then((response) => {
        if (searchId !== latestSearchId.current) return
        setSearchState({ status: 'success', results: response.data })
      })
      .catch(() => {
        if (searchId !== latestSearchId.current) return
        setSearchState({ status: 'error', results: [] })
      })
  }, [])

  const searchSurveys = useMemo(
    () => debounce(performSearch, 300),
    [performSearch]
  )

  useEffect(() => {
    return () => { searchSurveys.cancel() }
  }, [searchSurveys])

  const selectSurvey = (survey: SurveyResult): void => {
    latestSearchId.current += 1
    searchSurveys.cancel()
    setSearchState(IDLE)
    onSelect(survey)
  }

  return (
    <div id='phonerep-select-survey-form'>
      <label htmlFor='survey_search'>Select Survey (if applicable)</label>
      <input
        id='survey_search'
        name='survey_search'
        type='text'
        placeholder='Search Surveys'
        autoComplete='off'
        onChange={(e) => { searchSurveys(e.target.value) }}
      />
      {searchState.status === 'searching' && <p>Searching…</p>}
      {searchState.status === 'error' && <p>Could not search surveys. Please try again.</p>}
      <div id='survey-search-results'>
        {searchState.results.map((survey) => (
          <div
            key={survey.id}
            data-survey-id={survey.id}
            role='button'
            tabIndex={0}
            onClick={() => { selectSurvey(survey) }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                selectSurvey(survey)
              }
            }}
          >
            {survey.name}
          </div>
        ))}
      </div>
    </div>
  )
}

export default SurveySearch
